import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import cx from 'classnames';
import {
  addQuestionChild,
  deleteQuestionChild,
  findQuestion,
  getDefaultValues,
  restoreQuestionChild,
  validateCondition,
} from '@/common/containers/FormConstructor/utils';
import { RadioGroup } from '@/common/components/Inputs/index';
import DataEntryForm from '@/common/containers/FormConstructor/components/DataEntryForm';
import styles from '@/common/containers/FormConstructor/styles.less';
import MultipleInput from '@/common/containers/FormConstructor/components/MultipleInput';
import DataEntrySelect from '@/common/containers/FormConstructor/components/DataEntrySelect';
import { FormConstructorContext } from '@/common/containers/FormConstructor/FormConstructor';
import { ReactBB } from '@/common/utils/index';
import { useLocalizedField } from '@/common/hooks/index';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';

const FieldsConstructor = ({
  questions: _questions = [],
  onFormChange = () => {},
}) => {
  const { relativeAnswers, allAnswers, archivedAnswers } = useContext(
    FormConstructorContext
  );
  const { t } = useTranslation();
  const getLocalizedField = useLocalizedField();
  const [questions, setQuestions] = useState(_questions);
  const [fields, setFields] = useState([]);
  const [archivedEntries, setArchivedEntries] = useState({});
  const conditionalInputs = useRef({});
  const formContext = useFormContext();
  const { register, unregister, getValues, setValue, watch } = formContext;

  useEffect(() => {
    setQuestions(_questions);

    const nextArchivedEntries = Object.entries(archivedAnswers || {}).reduce(
      (acc, [slug, answersByUid]) => {
        const question = findQuestion(slug, _questions);

        if (!question?.parent) {
          return acc;
        }

        acc[slug] = Object.entries(answersByUid).map(([uid, answer]) => ({
          uid,
          slug,
          parentSlug: question.parent.slug,
          question,
          answer,
          deleteType: 'archive',
          restoreAction: 'restore',
        }));

        return acc;
      },
      {}
    );

    setArchivedEntries(nextArchivedEntries);

    Object.entries(nextArchivedEntries).forEach(([slug, entries]) => {
      (entries || []).forEach((entry) => {
        setValue(`${slug}.${entry.uid}.value`, entry.answer.value, {
          shouldDirty: false,
        });
      });
    });
  }, [_questions, archivedAnswers]);

  const getForm = useCallback(
    (questions, isChildren) => {
      const form = [];

      questions.forEach((question, number) => {
        const id = isChildren ? question.uid : null;
        const item = {
          element: getInput(question, {
            id,
            number: number + 1,
            listLength: questions.length,
          }),
          children: [],
          name: id ? `${question.slug}.${id}` : question.slug,
          canAddNew: question.answerOptions.can_add_new,
          slug: question.slug,
          header: getLocalizedField(question.answerOptions, 'header'),
          content: getLocalizedField(question, 'content'),
          subsection: question.subsection,
        };

        if (question.children.length) {
          item.children = getForm(question.children, true);
        }

        form.push(item);
      });

      return form;
    },
    [getLocalizedField]
  );

  const addNewEntry = useCallback(
    (slugKey) => {
      const question = findQuestion(slugKey, questions);
      const nextQuestions = addQuestionChild(questions, question);

      setFields(getForm(nextQuestions));
      setQuestions(nextQuestions);
      return nextQuestions;
    },
    [getForm, questions]
  );

  const deleteDataEntry = useCallback(
    (inputName, parentSlug) => {
      const [slug, uid] = inputName.split('.');
      const currentEntry = getValues(inputName) || {};
      const savedAnswer =
        allAnswers[slug]?.[uid] ||
        (currentEntry?.answerId
          ? {
              answerId: currentEntry.answerId,
              questionId: currentEntry.questionId,
              uid,
              value: currentEntry.value,
            }
          : null);

      if (savedAnswer) {
        const question = findQuestion(slug, questions);
        const currentValue =
          getValues(`${inputName}.value`) || savedAnswer.value;
        const restoreAction =
          currentEntry?.__action === 'restore' ? 'restore' : 'none';

        if (restoreAction === 'restore') {
          unregister(`__archivedActions.${slug}.${uid}`);
        } else {
          setValue(
            `__archivedActions.${slug}.${uid}`,
            {
              answerId: savedAnswer.answerId,
              questionId: savedAnswer.questionId,
              action: 'archive',
              value: '',
            },
            { shouldDirty: true }
          );
        }

        setArchivedEntries((prev) => ({
          ...prev,
          [slug]: [
            ...(prev[slug] || []),
            {
              uid,
              slug,
              parentSlug,
              question,
              answer: { ...savedAnswer, value: currentValue },
              deleteType: 'archive',
              restoreAction,
            },
          ],
        }));
        setValue(`${slug}.${uid}.value`, currentValue, {
          shouldDirty: false,
        });
      }

      const nextQuestions = deleteQuestionChild(questions, parentSlug, uid);
      unregister(inputName, { keepDirty: true });
      setFields(getForm(nextQuestions));
      setQuestions(nextQuestions);
      return nextQuestions;
    },
    [allAnswers, getForm, getValues, questions, setValue, unregister]
  );

  const restoreDataEntry = useCallback(
    (entry) => {
      const restoredQuestion = {
        ...entry.question,
        uid: entry.uid,
        children: entry.question.children || [],
      };
      const nextQuestions = restoreQuestionChild(questions, restoredQuestion);
      const restoredValue =
        entry.restoreAction === 'restore'
          ? {
              value: entry.answer.value,
              answerId: entry.answer.answerId,
              questionId: entry.answer.questionId,
              __action: 'restore',
            }
          : {
              value: entry.answer.value,
            };

      unregister(`__archivedActions.${entry.slug}.${entry.uid}`);
      setValue(`${entry.slug}.${entry.uid}`, restoredValue, {
        shouldDirty: true,
      });
      setArchivedEntries((prev) => ({
        ...prev,
        [entry.slug]: (prev[entry.slug] || []).filter(
          (item) => item.uid !== entry.uid
        ),
      }));
      setFields(getForm(nextQuestions));
      setQuestions(nextQuestions);
    },
    [getForm, questions, setValue]
  );

  /**
   * Define react elem on question data
   */
  const getInput = (
    {
      answerType,
      answerOptions: _answerOptions,
      answerOptionsFr: _answerOptionsFr,
      slug,
      isRequired,
      condition,
    },
    { id, number, listLength }
  ) => {
    const key = slug;
    let path;
    const answerOptions = getLocalizedField(
      { _answerOptions, _answerOptionsFr },
      '_answerOptions'
    );

    if (condition) {
      let invalid = false;
      Object.entries(condition).forEach(([fieldName, operation]) => {
        conditionalInputs.current[fieldName] = true;
        if (
          !validateCondition(fieldName, operation, getValues(), relativeAnswers)
        ) {
          unregister(key);
          invalid = true;
        }
      });
      if (invalid) return null;
    }

    if (answerType === 'single') {
      register(key);
      register(`${key}.value`);
    } else if (['multiple', 'DecCustodyInfo'].includes(answerType)) {
      register(key);
    } else {
      path = id ? `${key}.${id}` : key;
      register(path);
      register(`${path}.value`, {
        value: !getValues(`${path}.value`)
          ? getDefaultValues(answerOptions.properties)
          : getValues(`${path}.value`),
      });
    }

    const rules = {
      required: isRequired ? 'Field is required' : false,
      maxLength: 150,
    };

    switch (answerType) {
      case 'single': {
        return (
          <RadioGroup
            key={slug}
            options={answerOptions}
            name={`${key}.value`}
            rules={rules}
          />
        );
      }
      case 'multiple': {
        return (
          <MultipleInput
            key={slug}
            options={answerOptions}
            name={key}
            rules={rules}
          />
        );
      }
      case 'DecCustodyInfo': {
        return (
          <DataEntrySelect
            key={slug}
            entryName="DecDependData"
            required={isRequired}
            name="DecCustodyInfo.firstName"
          />
        );
      }
      default: {
        const currentEntry = path ? getValues(path) : null;
        return (
          <DataEntryForm
            key={path}
            parentKey={path}
            canDelete={listLength > 1 && answerOptions.can_add_new}
            deleteType={
              (allAnswers[slug] && id && allAnswers[slug][id]) ||
              currentEntry?.answerId
                ? 'archive'
                : 'delete'
            }
            number={number}
            {...answerOptions}
          />
        );
      }
    }
  };

  const renderElements = useCallback(() => {
    const renderArchivedEntries = (parentSlug) => {
      const entries = Object.values(archivedEntries).flatMap((items) =>
        (items || []).filter((entry) => entry.parentSlug === parentSlug)
      );

      return entries.map((entry, index) => (
        <div
          key={`${entry.slug}.${entry.uid}.archived`}
          className={cx(styles.question, styles.childrenItem)}
        >
          <div className={styles.questionBody}>
            <DataEntryForm
              {...entry.question.answerOptions}
              canRestore
              isArchived
              number={index + 1}
              onRestore={() => restoreDataEntry(entry)}
              parentKey={`${entry.slug}.${entry.uid}`}
              value={entry.answer.value}
            />
          </div>
        </div>
      ));
    };

    const renderChildren = (children, parentNum, parentSlug) => {
      const visibleChildren = children.filter(({ element }) => !!element);

      return (
        <div className={styles.children}>
          {children.map(
            (
              {
                element,
                name,
                canAddNew,
                slug,
                header,
                content,
                children: _children,
              },
              ind
            ) => {
              if (!element) return null;
              const nextNum = `${parentNum}.${ind + 1}`;
              return (
                <div
                  key={name}
                  className={cx(styles.question, styles.childrenItem)}
                >
                  {!canAddNew && children.length > 1 && (
                    <div className={styles.questionNumber}>{nextNum}.</div>
                  )}
                  <div className={styles.questionBody} id={`${slug}-anchor`}>
                    {!canAddNew && content && (
                      <div className={styles.questionBodyTitle}>
                        {ReactBB(content)}
                      </div>
                    )}
                    {React.cloneElement(element, {
                      onDelete: () => deleteDataEntry(name, parentSlug),
                    })}
                    {canAddNew && ind === children.length - 1 && (
                      <Button
                        color="primary"
                        size="medium"
                        variant="text"
                        startIcon={<AddIcon />}
                        sx={{ width: 'fit-content', mt: 6 }}
                        onClick={() => addNewEntry(slug)}
                      >
                        {t('Add')} {header}
                      </Button>
                    )}
                    {_children.length > 0 &&
                      renderChildren(_children, nextNum, slug)}
                  </div>
                </div>
              );
            }
          )}
          {visibleChildren.length > 0 && renderArchivedEntries(parentSlug)}
        </div>
      );
    };

    let number = 0;
    return (
      <div>
        {fields.map(
          ({ element, children, name, content, slug, subsection }) => {
            if (!element) return null;
            number += 1;
            return (
              <React.Fragment key={name}>
                {subsection && (
                  <p className={styles.subsectionTitle}>{subsection}</p>
                )}
                <div className={styles.question}>
                  <div className={styles.questionNumber}>{number}.</div>
                  <div className={styles.questionBody} id={`${slug}-anchor`}>
                    {content && (
                      <div className={styles.questionBodyTitle}>
                        {ReactBB(content)}
                      </div>
                    )}
                    {element}
                    {children.length > 0 &&
                      renderChildren(children, `${number}`, slug)}
                  </div>
                </div>
              </React.Fragment>
            );
          }
        )}
      </div>
    );
  }, [
    addNewEntry,
    archivedEntries,
    deleteDataEntry,
    fields,
    restoreDataEntry,
    t,
  ]);

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      onFormChange(value, { name, type }, formContext);
      if (type === 'change' && conditionalInputs.current[name.split('.')[0]]) {
        setFields(getForm(questions));
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, questions, onFormChange, formContext, getForm]);

  useEffect(() => {
    setFields(getForm(questions));
  }, [getForm, questions, archivedEntries]);

  return renderElements();
};

export default FieldsConstructor;
